import {
  Card,
  createIcon,
  Grid,
  GridItem,
  HStack,
  Icon,
  Stack,
  Text,
} from '@chakra-ui/react';
import { CSSProperties, ReactElement } from 'react';
import { useAppSelector, useAppDispatch } from '../../../lib/hooks';

import { Tooltip } from '../../../components/ui/tooltip';
import { setDocumentFilterHUDOC } from '@/lib/state/slices/ProjectStateSlice';
import { Conclusion, DecisionLevel, FilterHUDOC, filterObject, Importance } from '@/lib/state/ViewTypes';
import { HelpCircle } from 'react-feather';

const CircleIcon = createIcon({
  displayName: 'CircleIcon',
  viewBox: '0 0 24 24',
  path: <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="currentColor" />,
});

const CircleOutlineIcon = createIcon({
  displayName: 'CircleIcon',
  viewBox: '0 0 24 24',
  path: <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />,
});


export default function QuickFilter(props: { style: CSSProperties }) {

  let dispatch = useAppDispatch();

  const documentFilter = useAppSelector((state) => state.viewReducer.documents.filter.source.hudoc);

  const toggleImportanceOption = (value: string) => {
    const newOptions = documentFilter.importance.includes(value)
      ? documentFilter.importance.filter((e) => (e as never) !== value)
      : [...documentFilter.importance, value];

    dispatch(
      setDocumentFilterHUDOC({
        type: 'importance',
        value: newOptions,
      }),
    );
  };

  const toggleConclusionOption = (value: string) => {
    const newOptions = documentFilter.conclusion.includes(value)
      ? documentFilter.conclusion.filter((e) => (e as never) !== value)
      : [...documentFilter.conclusion, value];

    dispatch(
      setDocumentFilterHUDOC({
        type: 'conclusion',
        value: newOptions,
      }),
    );
  };

  const toggleDecisionOption = (value: string) => {
    const newOptions = documentFilter.decisionLevel.includes(value)
      ? documentFilter.decisionLevel.filter((e) => (e as never) !== value)
      : [...documentFilter.decisionLevel, value];

    dispatch(
      setDocumentFilterHUDOC({
        type: 'decisionLevel',
        value: newOptions,
      }),
    );
  }

  return (
    <Card.Root
      background="cardDefault"
      style={{
        ...props.style,
        padding: '10px',
      }}
    >
      <Grid templateRows="repeat(2, 1fr)" gap={2}>
        <QuickFilterOption
          data={Importance}
          type="importance"
          filter={documentFilter}
          icon={<CircleIcon />}
          onClick={toggleImportanceOption}
        />

        <QuickFilterOption
          data={Conclusion}
          type="conclusion"
          filter={documentFilter}
          icon={<CircleOutlineIcon />}
          onClick={toggleConclusionOption}
        />

        <QuickFilterOption
          data={DecisionLevel}
          type="decisionLevel"
          filter={documentFilter}
          icon={<CircleIcon />}
          onClick={toggleDecisionOption}
        />
      </Grid>
    </Card.Root>
  );
}

type QuickFilterProps = {
  data: filterObject;
  type: keyof FilterHUDOC;
  filter: FilterHUDOC;
  icon: ReactElement;
};

function QuickFilterOption(props: QuickFilterProps & { onClick: (index: string) => void }) {

  const { data, type, filter, icon, onClick } = props;

  return (
    <GridItem colSpan={1} rowSpan={2}>
      <Stack direction="row" alignItems="center">
        <Text fontSize="14px" color="secondaryText">{type}</Text>
        <Tooltip
          content={<Text>Filter out judgements based on the case details.</Text>}
          positioning={{ placement: 'right' }}
        >
          <Icon
            boxSize="14px"
            color="secondaryText"
          >
            <HelpCircle />
          </Icon>
        </Tooltip>
      </Stack>

      {Object.keys(data).map((index) => (
        <QuickFilterOptionItems
          key={`quickfilter_${type}_option_${index}`}
          index={index}
          data={data}
          type={type}
          filter={filter}
          icon={icon}
          onClick={() => onClick(index)}
        />
      ))}
    </GridItem>
  );
}

function QuickFilterOptionItems(props: QuickFilterProps & { index: string; onClick: () => void; }) {

  const { data, type, filter, icon, index, onClick } = props;

  return (
    <HStack alignItems="center" onClick={() => onClick()} cursor="pointer">
      <Icon
        boxSize="17px"
        color={
          filter[type].includes(index) ?
            data[index].color ?
              data[index].color :
              "primaryText" :
            'gray.400'
        }
      >
        {icon}
      </Icon>
      <Tooltip content={data[index].tooltip ? data[index].tooltip : data[index].label} positioning={{ placement: 'right' }}>
        <Text
          color={filter[type].includes(index) ? 'secondaryText' : 'gray.400'}
          userSelect="none"
          fontSize="14px"
        >
          {data[index].label}
        </Text>
      </Tooltip>
    </HStack>
  );
}
